import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Table, Button } from "react-bootstrap";
import "./StudentPerformance.css";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import DashboardStats from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { useGetStudentDetailByIdForSubSchool } from "../../../../hooks/organization/report/useGetStudentDetailByIdForSubSchool";
import "../../../../common/styles/table.css";

/**
 * @param {Object} props - Component props
 * @param {Object} props.student - Student object with id
 * @param {Function} props.onBack - Callback to go back to student list
 */
const StudentPerformance = ({ student, onBack }) => {
  // Get schoolId from URL params (parent SchoolDetail route)
  const { id } = useParams();
  const studentId = student?.id;
  const schoolId = id;

  const { loading, error, studentData, getStudentDetailById } =
    useGetStudentDetailByIdForSubSchool();

  // Fetch student data on mount
  useEffect(() => {
    if (schoolId && studentId) {
      getStudentDetailById(schoolId, studentId);
    }
  }, [schoolId, studentId]);

  const handleRefresh = () => {
    if (schoolId && studentId) {
      getStudentDetailById(schoolId, studentId);
    }
  };

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const studentDetail = studentData?.student;
  const questsByCategory = studentData?.quests_by_category || {};

  // Flatten all quests from all categories for the simplified table view
  const allQuests = Object.values(questsByCategory).flat();

  // Check if initial load (no data yet)
  const isInitialLoad = studentData === undefined;

  // Stats data for DashboardStats component
  const stats = [
    {
      label: "Overall Progress %",
      value:
        loading && isInitialLoad
          ? "..."
          : `${studentDetail?.overall_progress || 0}%`,
      icon: "fa-chart-line",
      colorClass: "dashboard-stat-card-blue",
    },
    {
      label: "Average Percentage",
      value:
        loading && isInitialLoad ? "..." : studentDetail?.avg_quiz_score || 0,
      icon: "fa-percent",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Total Quests Assigned",
      value:
        loading && isInitialLoad
          ? "..."
          : String(studentDetail?.total_quests_assigned || 0).padStart(2, "0"),
      icon: "fa-list-check",
      colorClass: "dashboard-stat-card-purple",
    },
  ];

  // Profile Skeleton Component
  const ProfileSkeleton = () => (
    <div className="student-profile-card">
      <div className="student-profile-left">
        <div className="skeleton-box skeleton-avatar"></div>
        <div className="student-info">
          <div
            className="skeleton-box"
            style={{ width: 150, height: 24, marginBottom: 8 }}
          ></div>
          <div
            className="skeleton-box"
            style={{ width: 120, height: 16, marginBottom: 6 }}
          ></div>
          <div
            className="skeleton-box"
            style={{ width: 80, height: 16, marginBottom: 6 }}
          ></div>
          <div
            className="skeleton-box"
            style={{ width: 180, height: 16 }}
          ></div>
        </div>
      </div>
      <div className="student-profile-right">
        <div
          className="skeleton-box"
          style={{ width: 140, height: 40, borderRadius: 5 }}
        ></div>
      </div>
    </div>
  );

  // Show skeleton on initial load
  if (loading && isInitialLoad) {
    return (
      <div className="student-performance-container">
        <div className="pt-2">
          <button className="back-btn" onClick={onBack}>
            <i className="bi bi-arrow-left"></i> Back to Students
          </button>
        </div>
        <ProfileSkeleton />
        <DashboardStatsSkeleton count={3} />
        <TableSkeleton rows={5} columns={3} />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="student-performance-container">
        <div className="pt-2">
          <button className="back-btn" onClick={onBack}>
            <i className="bi bi-arrow-left"></i> Back to Students
          </button>
        </div>
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      </div>
    );
  }

  // Show 404 if no data found
  if (!loading && studentData === null) {
    return (
      <div className="student-performance-container">
        <div className="pt-2">
          <button className="back-btn" onClick={onBack}>
            <i className="bi bi-arrow-left"></i> Back to Students
          </button>
        </div>
        <div className="text-center py-5">
          <h1 className="text-muted" style={{ fontSize: "72px" }}>
            404
          </h1>
          <h4 className="mb-3">Student Not Found</h4>
          <p className="text-muted mb-4">
            The student you're looking for doesn't exist or may have been
            removed.
          </p>
          <Button variant="primary" onClick={onBack}>
            Go Back to Students
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-performance-container">
      {/* Back Button */}
      <div className="pt-2">
        <button className="back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left"></i> Back to Students
        </button>
      </div>

      {/* Student Profile Header */}
      <div className="student-profile-card">
        <div className="student-profile-left">
          <div className="student-avatar-circle">
            {getInitials(studentDetail?.name)}
          </div>
          <div className="student-info">
            <h3>{studentDetail?.name || "-"}</h3>
            <div className="student-details-row">
              <span>
                Student ID: <strong>{studentDetail?.username || "-"}</strong>
              </span>
            </div>
            <div className="student-details-row">
              <span>
                Age: <strong>{studentDetail?.age || "-"}</strong>
              </span>
            </div>
            <div className="student-details-row">
              <span>
                Email: <strong>{studentDetail?.email || "-"}</strong>
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Stats Cards using DashboardStats */}
      <DashboardStats stats={stats} />

      {/* Quest Table - Simplified view */}
      {allQuests.length === 0 ? (
        <div className="alert alert-info mt-0" role="alert">
          No quests assigned to this student.
        </div>
      ) : (
        <div className="custom-table-container mt-0">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Quest Name</th>
                  <th>Episodes Completed</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {allQuests.map((quest) => (
                  <tr key={quest.quest_id}>
                    <td>
                      <div className="quest-name-cell">
                        <div className="quest-icon-placeholder">
                          <i className="bi bi-controller"></i>
                        </div>
                        {quest.quest_name}
                      </div>
                    </td>
                    <td className="text-center">{quest.episodes}</td>
                    <td className="text-center">{quest.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPerformance;
